import { useMemo } from 'react';
import {
  useClientStats,
  useClients,
  type AcquisitionSource,
  type ClientRow,
  type ClientStatsView,
} from '@busy/api';

// Aggregates clients by acquisition_source (migration 0075) to give
// the AcquisitionChannelTile a single per-source rollup. Surfaces the
// branding / marketing lever of the Busy Barber thesis: where do
// clients actually come from, and what's each channel worth.
//
// Returns one row per source plus a synthetic "untagged" bucket for
// clients without an acquisition_source set (legacy + barber-skipped).
//
// LTV is per-source average revenue across all visits from clients
// tagged to that source — a barber with 5 Instagram clients averaging
// $400 each ($2k total) vs. 5 walk-in clients averaging $80 each
// ($400 total) reads the channel quality difference instantly.

export type AcquisitionChannelRow = {
  source: AcquisitionSource | 'untagged';
  clientCount: number;
  /** % of all tagged clients in this channel. Untagged is included
   *  in the denominator so the visible bars reflect roster-wide
   *  distribution honestly. */
  sharePct: number;
  /** Sum of total_revenue_cents across all clients in this channel.
   *  Null when none of the channel's clients have any visit revenue. */
  totalRevenueCents: number | null;
  /** totalRevenueCents ÷ clientCount. Null when clientCount is 0
   *  (defensive — shouldn't happen with the filter, but the type
   *  stays honest). */
  avgLtvCents: number | null;
  /** Total visit count across the channel's clients. Surfaces in
   *  the tile as a secondary stat — useful for distinguishing
   *  "5 clients × 1 visit each" from "5 clients × 20 visits each". */
  visitCount: number;
};

export type AcquisitionChannelData = {
  rows: AcquisitionChannelRow[];
  totalClients: number;
  totalTaggedClients: number;
  /** True when fewer than 5 clients have been tagged at all — the
   *  tile uses this to render an empty-state CTA rather than a
   *  noisy 1-bar chart. */
  insufficient: boolean;
};

const ALL_SOURCES: AcquisitionSource[] = [
  'walkin',
  'referral',
  'instagram',
  'tiktok',
  'google',
  'yelp',
  'booking_platform',
  'other',
];

/** Pure helper — clients + stats → per-channel rollup. Exported for
 *  testing; the hook wraps with the data fetches. */
export function computeAcquisitionChannels(
  clients: ClientRow[],
  stats: ClientStatsView[],
): AcquisitionChannelData {
  // Build a quick lookup from client_id → ClientStatsView so we can
  // attach revenue + visit count to each source bucket without an N²
  // scan.
  const statsByClient = new Map<string, ClientStatsView>();
  for (const s of stats) statsByClient.set(s.client_id, s);

  // Skip archived clients — they shouldn't shape the channel rollup
  // (they're effectively retired). The tile is about *active*
  // acquisition pattern.
  const activeClients = clients.filter((c) => !c.archived_at);
  const totalClients = activeClients.length;

  // Initialize buckets for all 8 sources + untagged so rows always
  // render in a stable order (even at zero counts), then bucket each
  // client by their tag.
  const buckets: Record<AcquisitionSource | 'untagged', AcquisitionChannelRow> = {
    walkin: emptyRow('walkin'),
    referral: emptyRow('referral'),
    instagram: emptyRow('instagram'),
    tiktok: emptyRow('tiktok'),
    google: emptyRow('google'),
    yelp: emptyRow('yelp'),
    booking_platform: emptyRow('booking_platform'),
    other: emptyRow('other'),
    untagged: emptyRow('untagged'),
  };

  for (const c of activeClients) {
    const key: AcquisitionSource | 'untagged' =
      c.acquisition_source ?? 'untagged';
    const bucket = buckets[key];
    bucket.clientCount += 1;
    const stat = statsByClient.get(c.id);
    if (stat) {
      bucket.totalRevenueCents = (bucket.totalRevenueCents ?? 0) + stat.total_revenue_cents;
      bucket.visitCount += stat.visit_count;
    }
  }

  // Compute % share + avg LTV. Share denominator = totalClients (so
  // untagged shows the gap visually).
  const rows: AcquisitionChannelRow[] = [];
  let totalTagged = 0;
  for (const source of ALL_SOURCES) {
    const r = buckets[source];
    totalTagged += r.clientCount;
    rows.push(finalizeRow(r, totalClients));
  }
  // Untagged goes last regardless of count.
  rows.push(finalizeRow(buckets.untagged, totalClients));

  // Sort the 8 main sources by client count descending so the
  // biggest channels surface first. Untagged stays pinned at the end.
  const tagged = rows.slice(0, ALL_SOURCES.length);
  const untagged = rows[rows.length - 1]!;
  tagged.sort((a, b) => b.clientCount - a.clientCount);

  return {
    rows: [...tagged, untagged],
    totalClients,
    totalTaggedClients: totalTagged,
    insufficient: totalTagged < 5,
  };
}

function emptyRow(source: AcquisitionSource | 'untagged'): AcquisitionChannelRow {
  return {
    source,
    clientCount: 0,
    sharePct: 0,
    totalRevenueCents: null,
    avgLtvCents: null,
    visitCount: 0,
  };
}

function finalizeRow(r: AcquisitionChannelRow, denom: number): AcquisitionChannelRow {
  return {
    ...r,
    sharePct: denom > 0 ? (r.clientCount / denom) * 100 : 0,
    avgLtvCents:
      r.clientCount > 0 && r.totalRevenueCents != null
        ? Math.round(r.totalRevenueCents / r.clientCount)
        : null,
  };
}

/** Hook wrapper — fetches the live client roster + per-client stats
 *  and returns the per-channel aggregation. */
export function useAcquisitionChannels(): {
  data: AcquisitionChannelData | null;
  loading: boolean;
} {
  const clients = useClients();
  const stats = useClientStats();
  const data = useMemo(() => {
    if (clients.loading || stats.loading) return null;
    if (!clients.data) return null;
    return computeAcquisitionChannels(clients.data, stats.data ?? []);
  }, [clients.loading, stats.loading, clients.data, stats.data]);
  return { data, loading: clients.loading || stats.loading };
}
